import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { MdoReportAdvice } from '@common/customControls/bariatrics';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type {
    AdviesAan,
    ConclusieAanvullend,
    ConclusieAanvullendReadKey,
    ConclusieAanvullendReadLabel,
    IndicatieVerwijzing,
    IndicatieVerwijzingReadKey,
    IndicatieVerwijzingSectionTitle,
    IndicatieVoorVerwijzingReadLabel,
} from './../../utils';
import { conclusieAanvullendSectionItems, indicatieVerwijzingReadSection } from './../../utils';

export type BariPreMdoReportData = AdviesAan &
    ConclusieAanvullend &
    IndicatieVerwijzing & {
        datum_mdo: string | Date | null;
        inclusie_in_zorgpad: MdoReportAdvice | null;
        klaar_om_te_versturen: boolean;
        toelichting: string | null;
        verslag_bespreking_mdo: string | null;
        verwijsbrief_nodig: boolean | null;
    };

export type BariPreMdoReportReadKey =
    | keyof BariPreMdoReportData
    | ConclusieAanvullendReadKey
    | IndicatieVerwijzingReadKey;
export type BariPreMdoReportReadLabel =
    | 'Advies aan patiënt'
    | 'Advies aan huisarts'
    | 'Advies aan chirurg'
    | 'Conclusie'
    | ConclusieAanvullendReadLabel
    | 'Datum MDO'
    | IndicatieVoorVerwijzingReadLabel
    | 'Toelichting'
    | 'Verslag bespreking MDO'
    | 'Verwijsbrief nodig';
export type BariPreMdoReportReadSectionTitle = 'Conclusie' | IndicatieVerwijzingSectionTitle;

export const bariPreMdoReportReadSchema: TypedReadSchema<
    BariPreMdoReportReadKey,
    BariPreMdoReportReadLabel,
    BariPreMdoReportReadSectionTitle
>[] = [
    {
        items: [
            { key: 'datum_mdo', label: 'Datum MDO', type: 'dateAsString' },
            { key: 'verslag_bespreking_mdo', label: 'Verslag bespreking MDO' },
        ],
    },
    {
        items: [
            { key: 'inclusie_in_zorgpad', label: 'Conclusie' },
            { key: 'toelichting', label: 'Toelichting' },
            { key: 'verwijsbrief_nodig', label: 'Verwijsbrief nodig', type: 'boolean' },
            { key: 'advies_aan_patient', label: 'Advies aan patiënt' },
            { key: 'advies_aan_huisarts', label: 'Advies aan huisarts' },
            { key: 'advies_aan_chirurg', label: 'Advies aan chirurg' },
            ...conclusieAanvullendSectionItems,
        ],
        sectionTitle: 'Conclusie',
    },
    { ...indicatieVerwijzingReadSection },
];

const bariPreMdoReportFinal: TypedSchema<BariPreMdoReportData> = {
    advies_aan_chirurg: getTextAreaStringSchema(true),
    advies_aan_huisarts: getTextAreaStringSchema(true),
    advies_aan_patient: getTextAreaStringSchema(true),
    conclusie_aanvullend_diabetes_mellitus: Koi.boolean(),
    conclusie_aanvullend_hydrochloorthiazide: Koi.boolean(),
    conclusie_aanvullend_psychofarmaca: Koi.boolean(),
    conclusie_aanvullend_roken: Koi.boolean(),
    datum_mdo: Koi.koi().date(),
    inclusie_in_zorgpad: Koi.string().valid(...Object.values(MdoReportAdvice)),
    indicatie_voor_verwijzing_diabetes: Koi.boolean(),
    indicatie_voor_verwijzing_hartfalen: Koi.boolean(),
    indicatie_voor_verwijzing_hypertensie: Koi.boolean(),
    indicatie_voor_verwijzing_medicatie: Koi.boolean(),
    indicatie_voor_verwijzing_nefrologie: Koi.boolean(),
    indicatie_voor_verwijzing_obesitas_specifiek: Koi.boolean(),
    indicatie_voor_verwijzing_osteoporose: Koi.boolean(),
    indicatie_voor_verwijzing_overig: Koi.boolean(),
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema('indicatie_voor_verwijzing_overig'),
    klaar_om_te_versturen: Koi.boolean().valid(true),
    toelichting: getTextAreaStringSchema(),
    verslag_bespreking_mdo: getTextAreaStringSchema(),
    verwijsbrief_nodig: Koi.when('inclusie_in_zorgpad', {
        is: [MdoReportAdvice.continueWithGuidance, MdoReportAdvice.notYet],
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
};

// klaar_om_te_versturen was deliberately left out because we don't want that value to be saved in the draft
const bariPreMdoReportDraft: TypedSchema<Omit<BariPreMdoReportData, 'klaar_om_te_versturen'>> = {
    advies_aan_chirurg: getTextAreaStringSchema(true),
    advies_aan_huisarts: getTextAreaStringSchema(true),
    advies_aan_patient: getTextAreaStringSchema(true),
    conclusie_aanvullend_diabetes_mellitus: Koi.boolean(),
    conclusie_aanvullend_hydrochloorthiazide: Koi.boolean(),
    conclusie_aanvullend_psychofarmaca: Koi.boolean(),
    conclusie_aanvullend_roken: Koi.boolean(),
    datum_mdo: Koi.koi().date().allow(null),
    inclusie_in_zorgpad: Koi.string()
        .valid(...Object.values(MdoReportAdvice))
        .allow(null),
    indicatie_voor_verwijzing_diabetes: Koi.boolean(),
    indicatie_voor_verwijzing_hartfalen: Koi.boolean(),
    indicatie_voor_verwijzing_hypertensie: Koi.boolean(),
    indicatie_voor_verwijzing_medicatie: Koi.boolean(),
    indicatie_voor_verwijzing_nefrologie: Koi.boolean(),
    indicatie_voor_verwijzing_obesitas_specifiek: Koi.boolean(),
    indicatie_voor_verwijzing_osteoporose: Koi.boolean(),
    indicatie_voor_verwijzing_overig: Koi.boolean(),
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema(
        'indicatie_voor_verwijzing_overig',
        true
    ),
    toelichting: getTextAreaStringSchema(true),
    verslag_bespreking_mdo: getTextAreaStringSchema(true),
    verwijsbrief_nodig: Koi.boolean().allow(null),
};

export const bariPreMdoReportDraftSchema = Koi.object(bariPreMdoReportDraft);
export const bariPreMdoReportFinalSchema = Koi.object(bariPreMdoReportFinal);
